import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { cn } from '../../../../lib/utils';

interface SimpleTimePickerProps {
    value?: string; // HH:mm format
    onChange?: (time: string) => void;
    disabled?: boolean;
    className?: string;
}

export const SimpleTimePicker: React.FC<SimpleTimePickerProps> = ({
    value,
    onChange,
    disabled,
    className,
}) => {
    const [hour, setHour] = useState('09');
    const [minute, setMinute] = useState('00');
    const [openField, setOpenField] = useState<'hour' | 'minute' | null>(null);

    useEffect(() => {
        if (value) {
            const [h, m] = value.split(':');
            setHour(h || '09');
            setMinute(m || '00');
        } else {
            setHour('09');
            setMinute('00');
        }
    }, [value]);

    const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
    const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

    const handleHourChange = (h: string) => {
        setHour(h);
        onChange?.(`${h}:${minute}`);
        setOpenField(null);
    };

    const handleMinuteChange = (m: string) => {
        setMinute(m);
        onChange?.(`${hour}:${m}`);
        setOpenField(null);
    };

    return (
        <div
            className={cn(
                'flex items-center border border-[#e2e8f0] dark:border-gray-700 rounded-[4px] px-[6px] py-[2px] bg-white dark:bg-gray-900 relative h-[20px] flex-shrink-0',
                disabled && 'opacity-60 cursor-not-allowed',
                className
            )}
        >
            <Clock className="w-3.5 h-3.5 text-[#64748b] dark:text-gray-400 mr-[4px] flex-shrink-0" />
            <div className="flex items-center gap-0.5 min-w-0">
                <div className="relative flex-shrink-0">
                    <input
                        type="text"
                        value={hour}
                        readOnly
                        onClick={() => !disabled && setOpenField(openField === 'hour' ? null : 'hour')}
                        className="w-6 text-center border-none bg-transparent text-[12px] text-[#0a0a0a] dark:text-gray-200 focus:outline-none cursor-pointer leading-[18px] min-w-0"
                    />
                    {openField === 'hour' && (
                        <div className="absolute left-0 top-full z-50 bg-white dark:bg-gray-800 border border-[#e2e8f0] dark:border-gray-700 rounded-[6px] shadow-lg dark:shadow-gray-900/50 mt-1 max-h-32 overflow-y-auto min-w-[50px]">
                            {hours.map((h) => (
                                <div
                                    key={h}
                                    onClick={() => handleHourChange(h)}
                                    className="px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-center text-xs text-foreground"
                                >
                                    {h}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <span className="text-[12px] text-[#0a0a0a] dark:text-gray-200 px-0.5 leading-[18px] flex-shrink-0">:</span>
                <div className="relative flex-shrink-0">
                    <input
                        type="text"
                        value={minute}
                        readOnly
                        onClick={() => !disabled && setOpenField(openField === 'minute' ? null : 'minute')}
                        className="w-6 text-center border-none bg-transparent text-[12px] text-[#0a0a0a] dark:text-gray-200 focus:outline-none cursor-pointer leading-[18px] min-w-0"
                    />
                    {openField === 'minute' && (
                        <div className="absolute right-0 top-full z-50 bg-white dark:bg-gray-800 border border-[#e2e8f0] dark:border-gray-700 rounded-[6px] shadow-lg dark:shadow-gray-900/50 mt-1 max-h-32 overflow-y-auto min-w-[50px]">
                            {minutes.map((m) => (
                                <div
                                    key={m}
                                    onClick={() => handleMinuteChange(m)}
                                    className="px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-center text-xs text-foreground"
                                >
                                    {m}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

